import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetTruckTypes } from '../../../store/masterData/Actions';
import { formatDateTime, isNullOrWhiteSpace, upsertArrayElement } from '../../../services/appFunctionsService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import PackmanDialog from '../../../components/dialog/PackmanDialog';
import { useLocation } from 'react-router';
import { ConfirmationDialog, CustomTable } from '@zz2/zz2-ui';
import { v4 as uuidv4 } from 'uuid';
import { ITruckType, TruckType } from '../../../@types/model/masterData/truckType/truckType';
import { ITruckTypeFormValues, TruckTypeFormValues, truckTypeFormSchema } from '../../../@types/model/masterData/truckType/truckTypeFormValues';
import TruckTypeHttpService from '../../../services/http/masterData/truckTypeHttpService';
import TruckTypeForm from './form/TruckTypeForm';
import { setTruckTypeMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import { IApiError } from '../../../@types/other';

const TruckTypeScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const truckTypes = useAppSelector(state => state.masterData.truckTypes);
    const auth = useAppSelector(state => state.auth);
    const pathname = useLocation().pathname;

    const [isLoading, setIsLoading] = useState(false);
    const [selectedTruckType, setSelectedTruckType] = useState<ITruckType>();
    const [formGuid, setFormGuid] = useState<string>();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [deletingTruckType, setDeletingTruckType] = useState<ITruckType>();

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            } else {
                setIsLoading(true);
                try {
                    const res = await TruckTypeHttpService.getTruckTypeData();
                    dispatch(dataSetTruckTypes(res.data));
                } catch (error : unknown) {
                    const apiError = error as IApiError;
                    generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading truck types.');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchData();
    }, [dispatch]);

    const openDialog = useCallback(() => {
        setFormGuid(uuidv4());
        setIsDialogOpen(true);
    }, []);

    const closeDialog = useCallback(() => {
        setIsDialogOpen(false);
        setSelectedTruckType(undefined);
        setFormGuid(undefined);
    }, []);

    const editTruckType = useCallback((truckType : ITruckType) => {
        setSelectedTruckType(truckType);
        setFormGuid(truckType.guid);
        setIsDialogOpen(true);
    }, []);

    const onSubmit = useCallback(async (values : ITruckTypeFormValues) => {
        setIsLoading(true);
        let payload = { ...values };
        if (isNullOrWhiteSpace(payload.guid)) {
            payload.guid = formGuid ?? '';
        }

        try {
            // Validate the form data before sending to API
            const validationResult = truckTypeFormSchema.safeParse(payload);
            if (!validationResult.success) {
                console.error('Form validation failed:', validationResult.error.errors);
                generalShowErrorSnackbar('Please fix the validation errors before saving.');
                setIsLoading(false);
                return;
            }

            const res = await TruckTypeHttpService.addOrUpdateTruckType(TruckType(payload));
            const updatedList = upsertArrayElement(truckTypes, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetTruckTypes(updatedList));
            await setTruckTypeMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedTruckType ? 'Truck type updated successfully.' : 'Truck type added successfully.');
            closeDialog();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating truck type data.');
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, formGuid, truckTypes, selectedTruckType, closeDialog]);

    const openDeleteConfirmationPopup = useCallback((truckType : ITruckType) => {
        setIsDeletePopupOpen(true);
        setDeletingTruckType(truckType);
    }, []);

    const closeDeleteConfirmationPopup = useCallback(() => {
        setIsDeletePopupOpen(false);
        setDeletingTruckType(undefined);
    }, []);

    const removeTruckType = useCallback(async () => {
        if (!deletingTruckType) return;
        const updatedTruckType = { ...deletingTruckType, isActive: false };
        setIsLoading(true);
        try {
            const res = await TruckTypeHttpService.deleteTruckType(updatedTruckType.id);
            const updatedList = upsertArrayElement(truckTypes, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetTruckTypes(updatedList));
            await setTruckTypeMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Truck type deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting truck type.');
        } finally {
            closeDeleteConfirmationPopup();
            setIsLoading(false);
        }
    }, [deletingTruckType, dispatch, truckTypes, closeDeleteConfirmationPopup]);

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return TruckTypeFormValues(selectedTruckType);
        }
    }, [selectedTruckType, isDialogOpen]);

    const hasEditingRight = useMemo(() => {
        const rights = auth?.session?.user?.rights || [];
        return rights.some(x => pathname.includes(x.url) && x.isActive && x.code.endsWith('_EDIT'));
    }, [auth?.session?.user?.rights, pathname]);

    return (
        <Screen isLoading={isLoading}>
            {isDialogOpen && initialValues &&
                <PackmanDialog
                    title='Truck Type'
                    maxWidth='md'
                    isEdit={!!selectedTruckType}
                    isLoading={isLoading}
                    isOpen={isDialogOpen}
                    onClose={closeDialog}>
                    <TruckTypeForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={closeDialog}
                    />
                </PackmanDialog>
            }
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<ITruckType>
                        enableAdding={hasEditingRight}
                        addFunction={openDialog}
                        editFunction={editTruckType}
                        enableEditing={hasEditingRight}
                        enableDeleting={truckType => truckType.isActive && hasEditingRight}
                        deleteFunction={openDeleteConfirmationPopup}
                        enableSorting
                        enableFiltering
                        fitWidthToPage
                        enablePagination
                        columns={[
                            { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                            { title: 'Description', field: 'description', enableFiltering: true, enableSorting: true },
                            { title: 'Default No. Pallets on Truck', field: 'defaultNumberOfPallets', enableFiltering: true, enableSorting: true },
                            { title: 'Max Payload (kg)', field: 'maxPayload', enableFiltering: true, enableSorting: true },
                            { title: 'Weight of Truck (kg)', field: 'weightOfTruck', enableFiltering: true, enableSorting: true },
                            { title: 'Tare Weight (kg)', field: 'tareWeight', enableFiltering: true, enableSorting: true },
                            { title: 'Percentage Warning', field: 'percentageWarning', enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        rows={truckTypes}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            <ConfirmationDialog
                title='Delete Truck Type'
                open={isDeletePopupOpen}
                description='Are you sure you want to delete this truck type?'
                onAccept={removeTruckType}
                onClose={closeDeleteConfirmationPopup}
                dialogType='orange'
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default TruckTypeScreen;
